import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  FileText,
  Search,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  Eye,
  PenTool,
  Download,
  Bell,
  Mail
} from "lucide-react";
import { Policy, Memo, PolicyAssignment, Document } from "../types";
import { toSydneyDate } from "../utils/helpers";

export {};

// Helper functions to handle different property names
const getDocumentTitle = (document: Document, documentType: 'policy' | 'memo') => {
  if (documentType === 'policy') {
    return document.title;
  } else {
    return document.subject;
  }
};

const getDocumentDescription = (document: Document, documentType: 'policy' | 'memo') => {
  if (documentType === 'policy') {
    return document.description;
  } else {
    // Memos don't have description field, use subject or empty string
    return '';
  }
};

const getDocumentFileUrl = (document: Document, documentType: 'policy' | 'memo') => {
  if (documentType === 'policy') {
    return document.fileUrl;
  } else {
    // Memos have attachments instead of fileUrl
    const memo = document;
    return memo.attachments && memo.attachments.length > 0 ? memo.attachments[0].url : undefined;
  }
};

// Get document status for current user (moved outside to prevent recreation)
const getDocumentStatus = (
  document: Document,
  documentType: 'policy' | 'memo',
  policyAssignments: any[],
  memoAssignments: any[],
  getUserPolicySignature: (policyId: string, userId: string) => any,
  getUserMemoSignature: (memoId: string, userId: string) => any,
  userId: string
) => {
  if (documentType === 'policy') {
    const policy = document;
    const assignment = policyAssignments.find(a => {
      const assignmentPolicyId = typeof a.policy?.id === 'object' ? (a.policy?.id as any).id : a.policy?.id;
      return assignmentPolicyId == policy.id && a.user.id == userId;
    });
    const signature = getUserPolicySignature(policy.id || '', userId);

    const requiresSignature = policy.requireSignature;
    const isRead = assignment?.readDate ? true : false;
    const isSigned = signature !== null && signature !== undefined;
    const isCompleted = assignment?.status === 'Completed';

    if (requiresSignature) {
      if (isSigned) {
        return {
          status: 'completed',
          label: 'Signed',
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          action: 'View Signature'
        };
      } else {
        return {
          status: 'pending-signature',
          label: 'Pending Signature',
          icon: PenTool,
          color: 'text-orange-500',
          bgColor: 'bg-orange-50',
          action: 'Sign Now'
        };
      }
    } else {
      if (isRead || isCompleted) {
        return {
          status: 'read',
          label: 'Read',
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          action: 'View Again'
        };
      } else {
        return {
          status: 'unread',
          label: 'New',
          icon: AlertCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          action: 'Read Now'
        };
      }
    }
  } else {
    const memo = document;
    const assignment = memoAssignments.find(a => {
      const assignmentMemoId = typeof a.memo?.id === 'object' ? (a.memo?.id as any).id : a.memo?.id;
      return assignmentMemoId == memo.id && a.user.id == userId;
    });
    const signature = getUserMemoSignature(memo.id || '', userId);

    const requiresSignature = memo.requireSignature;
    const isRead = assignment?.readDate ? true : false;
    const isSigned = signature !== null && signature !== undefined;
    const isCompleted = assignment?.status === 'Completed';

    if (requiresSignature) {
      if (isSigned) {
        return {
          status: 'completed',
          label: 'Signed',
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          action: 'View Signature'
        };
      } else {
        return {
          status: 'pending-signature',
          label: 'Pending Signature',
          icon: PenTool,
          color: 'text-orange-500',
          bgColor: 'bg-orange-50',
          action: 'Sign Now'
        };
      }
    } else {
      if (isRead || isCompleted) {
        return {
          status: 'read',
          label: 'Read',
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          action: 'View Again'
        };
      } else {
        return {
          status: 'unread',
          label: 'New',
          icon: AlertCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          action: 'Read Now'
        };
      }
    }
  }
};

export default function MyPoliciesPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    policies,
    policyAssignments,
    memos,
    memoAssignments,
    getPoliciesForUser,
    getMemosForUser,
    getUserPolicySignature,
    getUserMemoSignature,
    refreshUserAssignments
  } = useData();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [userPolicies, setUserPolicies] = useState<PolicyAssignment[]>([]);

  const hasLoadedRef = useRef(false);

  // Refresh policy and memo assignments when component mounts (only once)
  useEffect(() => {
    const fetchUserPolicyAssignments = async() => {
      if (currentUser?.id && !hasLoadedRef.current) {
        console.log('[MyPolicies] Loading assignments for first time...');
        hasLoadedRef.current = true;
        await refreshUserAssignments();

        setUserPolicies(policyAssignments);
      }
    }

    fetchUserPolicyAssignments();
  }, []); // No dependencies - only run on mount

  if (!currentUser) return null;

  const userMemos = getMemosForUser(currentUser?.id || '');

  const allDocuments = useMemo(() => {
    if (!currentUser?.id || !policyAssignments || !memoAssignments) {
      return [];
    }

    const mappedPolicies = policyAssignments
      .filter(assignment => assignment.user?.id == currentUser.id && assignment.policy)
      .map(assignment => {
        return {
          ...assignment.policy,
          documentType: 'policy' as const,
          assignment: assignment,
        };
      });

    const mappedMemos = memoAssignments
      .filter(assignment => assignment.user?.id == currentUser.id && assignment.memo)
      .map(assignment => {
        return {
          ...assignment.memo,
          documentType: 'memo' as const,
          assignment: assignment,
        };
      });

    return [...mappedPolicies, ...mappedMemos];

  }, [currentUser?.id, policyAssignments, memoAssignments]);

  const currentUserIdRef = useRef(currentUser?.id);
  currentUserIdRef.current = currentUser?.id;

  const documentsWithStatus = useMemo(() => {
    const userId = currentUserIdRef.current;
    if (!userId) return [];

    return allDocuments.map(document => {
      const status = getDocumentStatus(
        document,
        document.documentType,
        policyAssignments,
        memoAssignments,
        getUserPolicySignature,
        getUserMemoSignature,
        userId
      );
      return { ...document, calculatedStatus: status };
    });
  }, [allDocuments, policyAssignments, memoAssignments]);

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return {
          label: 'Critical',
          variant: 'destructive',
          className: 'bg-red-200 text-red-600 border-red-600'
        };
      case 'High':
        return {
          label: 'High',
          variant: 'destructive',
          className: 'bg-orange-200 text-orange-600 border-orange-600'
        };
      case 'Med':
        return {
          label: 'Medium',
          variant: 'secondary',
          className: 'bg-yellow-200 text-yellow-600 border-yellow-600'
        };
      case 'Low':
        return {
          label: 'Low',
          variant: 'secondary',
          className: 'bg-blue-200 text-blue-600 border-blue-600'
        };
      default:
        return {
          label: 'Medium',
          variant: 'secondary',
          className: 'bg-yellow-200 text-yellow-600 border-yellow-600'
        };
    }
  };

  const filteredDocuments = useMemo(() => {
    return documentsWithStatus.filter(document => {
      const title = getDocumentTitle(document, document.documentType as 'policy' | 'memo');
      const description = getDocumentDescription(document, document.documentType as 'policy' | 'memo');
      const matchesSearch = title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           description?.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesStatus = true;
      if (statusFilter !== "all") {
        matchesStatus = document.calculatedStatus.status === statusFilter;
      }

      let matchesType = true;
      if (typeFilter !== "all") {
        matchesType = document.documentType === typeFilter;
      }

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [documentsWithStatus.length, searchTerm, statusFilter, typeFilter]);

  const documentSummary = useMemo(() => {
    const summary = {
      total: documentsWithStatus.length,
      unread: 0,
      'pending-signature': 0,
      completed: 0,
      read: 0
    };

    documentsWithStatus.forEach(document => {
      summary[document.calculatedStatus.status as keyof typeof summary]++;
    });

    return summary;
  }, [documentsWithStatus.length]);

  // No local formatDate needed, using toSydneyDate from helpers

  const handleDocumentAction = (document: Document) => {
    if (document.documentType === 'policy') {
      navigate(`/my-policies/${document.id}/view`);
    } else {
      navigate(`/memo-sign/${document.id}`);
    }
  };

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">My Policies</h1>
        <p className="text-muted-foreground mt-1">
          View and acknowledge policies and memos assigned to you
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total Assigned</p>
              <p className="text-2xl font-semibold">{documentSummary.total}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-sm text-muted-foreground">Unread</p>
              <p className="text-2xl font-semibold">{documentSummary.unread}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <PenTool className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-sm text-muted-foreground">Pending Signature</p>
              <p className="text-2xl font-semibold">{documentSummary['pending-signature']}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-semibold">{documentSummary.completed + documentSummary.read}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search policies and memos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-auto">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="pending-signature">Pending Signature</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-auto">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="policy">Policies</SelectItem>
                  <SelectItem value="memo">Memos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <div className="space-y-4">
        {filteredDocuments.map((document, index) => {
          const documentStatus = document.calculatedStatus;
          const StatusIcon = documentStatus.icon;

          const assignment = document.documentType === 'policy'
            ? policyAssignments.find(a => {
                const assignmentPolicyId = typeof a.policy?.id === 'object' ? (a.policy?.id as any).id : a.policy?.id;
                return assignmentPolicyId === document.id && a.user?.id == (currentUser?.id || '');
              })
            : memoAssignments.find(a => {
                const assignmentMemoId = typeof a.memo?.id === 'object' ? (a.memo?.id as any).id : a.memo?.id;
                return assignmentMemoId === document.id && a.user?.id == (currentUser?.id || '');
              });

          const priorityBadge = getPriorityBadge(assignment?.assignmentPriority || 'Med');

          return (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${documentStatus.bgColor}`}>
                        <StatusIcon className={`h-5 w-5 ${documentStatus.color}`} />
                      </div>
                      <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-medium text-base md:text-lg">{getDocumentTitle(document, document.documentType as 'policy' | 'memo')}</h3>
                            <Badge variant="outline">{document.documentType === 'policy' ? 'Policy' : 'Memo'}</Badge>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {(() => {
                                const version = document.documentType === 'policy'
                                  ? (assignment as any)?.policyVersion || (document as Policy).version
                                  : (assignment as any)?.memoVersion || (document as Memo).version;
                                return `v${version}`;
                              })()}
                            </Badge>
                          <Badge
                            variant={documentStatus.status === 'completed' || documentStatus.status === 'read' ? 'default' : 'secondary'}
                          >
                            {documentStatus.label}
                          </Badge>
                          <Badge
                            variant={priorityBadge.variant as any}
                            className={priorityBadge.className}
                          >
                            {priorityBadge.label}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Effective: {toSydneyDate(document.effectiveDate || '')}
                          </div>
                          {document.expiryDate && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Expires: {toSydneyDate(document.expiryDate)}
                            </div>
                          )}
                          {documentStatus.status === 'completed' && (() => {
                            const signature = document.documentType === 'policy'
                              ? getUserPolicySignature(document.id || '', currentUser?.id || '')
                              : getUserMemoSignature(document.id || '', currentUser?.id || '');
                            return signature ? (
                              <div className="flex items-center gap-1">
                                <PenTool className="h-4 w-4" />
                                Signed: {toSydneyDate(signature.signedDate)}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleDocumentAction(document)}
                      variant={documentStatus.status === 'unread' || documentStatus.status === 'pending-signature' ? 'default' : 'outline'}
                      className="whitespace-nowrap"
                    >
                      {documentStatus.action}
                    </Button>
                                          
                      {/* Only show download button for policies, not memos */}
                      {document.documentType === 'policy' && getDocumentFileUrl(document, document.documentType as 'policy' | 'memo') && (
                        <Button asChild variant="ghost" size="sm" className="text-xs">
                          <a href={getDocumentFileUrl(document, document.documentType as 'policy' | 'memo') as string} target="_blank" rel="noopener noreferrer">
                            <Download className="h-3 w-3 mr-1" />
                            Download PDF
                          </a>
                        </Button>
                      )}
                  </div>
                </div>
                
                {/* Progress indicator for documents requiring signature */}
                {document.requireSignature && documentStatus.status === 'pending-signature' && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm">
                      <PenTool className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Digital signature required
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Signed status indicator */}
                {document.requireSignature && documentStatus.status === 'completed' && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-600">
                        Document signed and acknowledged
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredDocuments.length === 0 && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="mb-2">No documents found</h3>
                              <p className="text-muted-foreground">
                  {searchTerm || statusFilter !== "all" || typeFilter !== "all"
                    ? "Try adjusting your search or filters"
                    : "You don't have any documents assigned at the moment"
                  }
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}